import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { BlockquotePlugin } from '@udecode/plate-block-quote/react';
import { HEADING_KEYS } from '@udecode/plate-heading';
import { BulletedListPlugin, ListPlugin, NumberedListPlugin } from '@udecode/plate-list/react';
import { TableCellPlugin, TablePlugin, TableRowPlugin } from '@udecode/plate-table/react';
import { ParagraphPlugin, useEditorRef, useSelectionFragmentProp } from '@udecode/plate/react';
import { useMemo } from 'react';
import { Dropdown } from '../Dropdown';
import { MarkdownBulletListIcon, MarkdownH1Icon, MarkdownH2Icon, MarkdownH3Icon, MarkdownNumberedListIcon, MarkdownParagraphIcon, MarkdownQuoteIcon } from '../Icons';
import { Stack } from '../Layout';
import { theme } from '../theme';
import { useMarkdownProps } from './context';
import { StyledDropdownHandle, StyledToolbarArrowIcon } from './styled';
import { focusEditorAfterOperation } from './utils';
export const STRUCTURAL_TYPES = [
    TablePlugin.key,
    TableRowPlugin.key,
    TableCellPlugin.key
];
const typeMap = [
    {
        icon: /*#__PURE__*/ _jsx(MarkdownParagraphIcon, {
            type: "extended"
        }),
        keywords: [
            'paragraph'
        ],
        label: 'Text',
        value: ParagraphPlugin.key
    },
    {
        icon: /*#__PURE__*/ _jsx(MarkdownH1Icon, {
            type: "extended"
        }),
        keywords: [
            'title',
            'h1'
        ],
        label: 'Heading 1',
        value: HEADING_KEYS.h1
    },
    {
        icon: /*#__PURE__*/ _jsx(MarkdownH2Icon, {
            type: "extended"
        }),
        keywords: [
            'subtitle',
            'h2'
        ],
        label: 'Heading 2',
        value: HEADING_KEYS.h2
    },
    {
        icon: /*#__PURE__*/ _jsx(MarkdownH3Icon, {
            type: "extended"
        }),
        keywords: [
            'subtitle',
            'h3'
        ],
        label: 'Heading 3',
        value: HEADING_KEYS.h3
    },
    {
        icon: /*#__PURE__*/ _jsx(MarkdownBulletListIcon, {
            type: "extended"
        }),
        keywords: [
            'unordered',
            'ul',
            '-'
        ],
        label: 'Bulleted list',
        value: BulletedListPlugin.key
    },
    {
        icon: /*#__PURE__*/ _jsx(MarkdownNumberedListIcon, {
            type: "extended"
        }),
        keywords: [
            'ordered',
            'ol',
            '1'
        ],
        label: 'Numbered list',
        value: NumberedListPlugin.key
    },
    {
        icon: /*#__PURE__*/ _jsx(MarkdownQuoteIcon, {
            type: "extended"
        }),
        keywords: [
            'citation',
            'blockquote',
            '>'
        ],
        label: 'Quote',
        value: BlockquotePlugin.key
    }
];
const setBlockType = (editor, type, previousType)=>{
    if (type === BulletedListPlugin.key || type === NumberedListPlugin.key) {
        editor.getTransforms(ListPlugin).toggle.list({
            type
        });
        return;
    }
    // if previous type is list unlist it first
    if (previousType === BulletedListPlugin.key || previousType === NumberedListPlugin.key) {
        editor.getTransforms(ListPlugin).toggle.list({
            type: previousType
        });
    }
    editor.tf.withoutNormalizing(()=>{
        const setEntry = (entry)=>{
            const [node, path] = entry;
            if (node.type !== type) {
                editor.tf.setNodes({
                    type
                }, {
                    at: path
                });
            }
        };
        const entries = editor.api.blocks({
            mode: 'lowest'
        });
        entries.forEach((entry)=>setEntry(entry));
    });
};
export function TextTypeSelect() {
    const editor = useEditorRef();
    const { portalDropdowns = true } = useMarkdownProps();
    const value = useSelectionFragmentProp({
        defaultValue: ParagraphPlugin.key,
        structuralTypes: STRUCTURAL_TYPES,
        getProp: (node)=>node.type
    });
    const selectedItem = useMemo(()=>typeMap.find((item)=>item.value === (value ?? ParagraphPlugin.key)) ?? typeMap[0], [
        value
    ]);
    const onSelect = (type)=>{
        focusEditorAfterOperation(editor, ()=>{
            setBlockType(editor, type, value);
        });
    };
    return /*#__PURE__*/ _jsx(Dropdown, {
        "data-test-id": "markdown-editor-text-type-select-dropdown",
        portalled: portalDropdowns,
        overlay: typeMap.map(({ icon, label, value: itemValue })=>/*#__PURE__*/ _jsx(Dropdown.Item, {
                onSelect: ()=>onSelect(itemValue),
                "data-test-id": `markdown-editor-text-type-${itemValue}-dropdown-item-`,
                children: /*#__PURE__*/ _jsxs(Stack, {
                    direction: "horizontal",
                    gap: theme.spaces.x12,
                    children: [
                        icon,
                        label
                    ]
                })
            }, itemValue)),
        children: /*#__PURE__*/ _jsx(StyledDropdownHandle, {
            variant: "tertiaryGray",
            size: "m",
            icon: /*#__PURE__*/ _jsx(StyledToolbarArrowIcon, {
                type: "default",
                size: "xxs"
            }),
            "data-test-id": "markdown-editor-text-type-select-button",
            children: selectedItem.label
        })
    });
}
